if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.ldMql = window.matchMedia('(max-width: 1023px)');
        this.isLdGallery = this.classList.contains('ciw-pdp-gallery--ld');

        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        }

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        if (this.isLdGallery) {
          this.initLdGallery();
        }
      }

      initLdGallery() {
        this.dotsRoot = this.querySelector('.ciw-pdp-gallery-dots');
        this.viewerSlider = this.elements.viewer?.querySelector('[id^="Slider-Gallery"]');
        this.ldDesktopMql = window.matchMedia('(min-width: 1024px)');
        this.ldDragState = {
          active: false,
          startX: 0,
          startScrollLeft: 0,
          pointerId: null,
          lastMoveX: 0,
          lastMoveTime: 0,
          velocityX: 0,
        };
        this.ldWheelSnapTimer = null;

        if (this.viewerSlider) {
          this.viewerSlider.addEventListener('scroll', debounce(this.onLdGalleryScroll.bind(this), 80));
        }

        this.initLdDesktopDragScroll();

        this.ldMql.addEventListener('change', () => this.buildLdDots());
        this.buildLdDots();

        document.addEventListener('ciw:gallery-filtered', (event) => {
          const sectionId = this.id.replace('MediaGallery-', '');
          if (event.detail?.sectionId === sectionId) {
            window.setTimeout(() => {
              this.buildLdDots();
              this.snapLdGalleryToNearest(false);
            }, 50);
          }
        });
      }

      getVisibleGallerySlides() {
        if (!this.elements.viewer) return [];

        return Array.from(
          this.elements.viewer.querySelectorAll('.ci-gallery-slide, .product__media-item')
        ).filter(
          (slide) =>
            !slide.hasAttribute('hidden') &&
            slide.style.display !== 'none' &&
            !slide.classList.contains('ciw-gallery-slide--hidden')
        );
      }

      buildLdDots() {
        if (!this.dotsRoot) return;

        if (!this.ldMql.matches) {
          this.dotsRoot.hidden = true;
          this.dotsRoot.innerHTML = '';
          return;
        }

        const slides = this.getVisibleGallerySlides();
        if (slides.length <= 1) {
          this.dotsRoot.hidden = true;
          this.dotsRoot.innerHTML = '';
          return;
        }

        this.dotsRoot.hidden = false;
        this.dotsRoot.innerHTML = '';

        slides.forEach((slide, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'ciw-pdp-gallery-dots__btn';
          button.setAttribute('role', 'tab');
          button.setAttribute('aria-label', `Go to slide ${index + 1}`);
          if (index === 0) button.classList.add('is-active');
          button.setAttribute('aria-current', index === 0 ? 'true' : 'false');
          button.setAttribute('aria-selected', index === 0 ? 'true' : 'false');
          button.addEventListener('click', () => {
            if (this.viewerSlider) {
              this.scrollLdGalleryToSlide(this.viewerSlider, slide, true);
            }
            this.setActiveDot(index);
          });
          this.dotsRoot.appendChild(button);
        });
      }

      getNearestSlideIndex(slides, scrollLeft) {
        if (!slides.length) return 0;

        let nearestIndex = 0;
        let nearestDistance = Math.abs(slides[0].offsetLeft - scrollLeft);

        slides.forEach((slide, index) => {
          const distance = Math.abs(slide.offsetLeft - scrollLeft);
          if (distance < nearestDistance) {
            nearestDistance = distance;
            nearestIndex = index;
          }
        });

        return nearestIndex;
      }

      isLdDesktopGallery() {
        return this.isLdGallery && this.ldDesktopMql?.matches;
      }

      scrollLdGalleryToSlide(slider, slide, smooth = true) {
        if (!slider || !slide) return;
        const useSmooth = smooth && !this.isLdDesktopGallery();
        const targetLeft = Math.max(0, Math.round(slide.offsetLeft));
        slider.scrollTo({
          left: targetLeft,
          behavior: useSmooth ? 'smooth' : 'auto',
        });

        if (this.isLdDesktopGallery()) {
          this.correctLdGalleryScroll(slider, slide);
        }
      }

      correctLdGalleryScroll(slider, slide) {
        const align = () => {
          slider.scrollLeft = Math.max(0, Math.round(slide.offsetLeft));
        };

        requestAnimationFrame(() => {
          align();
          requestAnimationFrame(align);
        });

        slider.addEventListener('scrollend', align, { once: true });
      }

      onLdGalleryScroll() {
        if (!this.viewerSlider) return;

        const slides = this.getVisibleGallerySlides();
        if (slides.length <= 1) return;

        const activeIndex = this.getNearestSlideIndex(slides, this.viewerSlider.scrollLeft);

        if (this.ldMql.matches) {
          this.setActiveDot(activeIndex);
        }

        if (this.ldDesktopMql?.matches) {
          this.syncLdActiveSlide(slides[activeIndex]);
        }
      }

      setActiveDot(index) {
        if (!this.dotsRoot) return;

        this.dotsRoot.querySelectorAll('.ciw-pdp-gallery-dots__btn').forEach((button, buttonIndex) => {
          const isActive = buttonIndex === index;
          button.classList.toggle('is-active', isActive);
          button.setAttribute('aria-current', isActive ? 'true' : 'false');
          button.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });
      }

      initLdDesktopDragScroll() {
        const slider = this.viewerSlider;
        const dragSurface = this.elements.viewer || slider;
        if (!slider || !dragSurface) return;

        const setGrabbing = (isGrabbing) => {
          slider.classList.toggle('is-grabbing', isGrabbing);
          if (dragSurface !== slider) dragSurface.classList.toggle('is-grabbing', isGrabbing);
        };

        dragSurface.addEventListener('pointerdown', (event) => {
          if (!this.ldDesktopMql.matches) return;
          if (event.pointerType === 'mouse' && event.button !== 0) return;

          this.ldDragState.active = true;
          this.ldDragState.startX = event.clientX;
          this.ldDragState.startScrollLeft = slider.scrollLeft;
          this.ldDragState.pointerId = event.pointerId;
          this.ldDragState.lastMoveX = event.clientX;
          this.ldDragState.lastMoveTime = Date.now();
          this.ldDragState.velocityX = 0;
          setGrabbing(true);
          if (dragSurface.setPointerCapture) dragSurface.setPointerCapture(event.pointerId);
        });

        dragSurface.addEventListener('pointermove', (event) => {
          if (!this.ldDragState.active || event.pointerId !== this.ldDragState.pointerId) return;
          if (!this.ldDesktopMql.matches) return;

          event.preventDefault();
          const now = Date.now();
          const elapsed = now - this.ldDragState.lastMoveTime;
          if (elapsed > 0) {
            this.ldDragState.velocityX = (event.clientX - this.ldDragState.lastMoveX) / elapsed;
          }
          this.ldDragState.lastMoveX = event.clientX;
          this.ldDragState.lastMoveTime = now;

          const delta = event.clientX - this.ldDragState.startX;
          slider.scrollLeft = this.ldDragState.startScrollLeft - delta;
        });

        const finishDrag = (event) => {
          if (!this.ldDragState.active) return;
          if (event?.pointerId !== undefined && event.pointerId !== this.ldDragState.pointerId) return;

          this.ldDragState.active = false;
          setGrabbing(false);
          if (dragSurface.releasePointerCapture && this.ldDragState.pointerId !== null) {
            try {
              dragSurface.releasePointerCapture(this.ldDragState.pointerId);
            } catch (error) {
              /* pointer already released */
            }
          }
          this.ldDragState.pointerId = null;

          const slides = this.getVisibleGallerySlides();
          const swipeThreshold = 0.35;
          if (slides.length > 1 && Math.abs(this.ldDragState.velocityX) >= swipeThreshold) {
            const currentIndex = this.getNearestSlideIndex(slides, slider.scrollLeft);
            const targetIndex = Math.min(
              slides.length - 1,
              Math.max(0, currentIndex + (this.ldDragState.velocityX > 0 ? -1 : 1))
            );
            const targetSlide = slides[targetIndex];
            this.scrollLdGalleryToSlide(slider, targetSlide, !this.isLdDesktopGallery());
            this.syncLdActiveSlide(targetSlide);
            return;
          }

          this.snapLdGalleryToNearest(!this.isLdDesktopGallery());
        };

        dragSurface.addEventListener('pointerup', finishDrag);
        dragSurface.addEventListener('pointercancel', finishDrag);

        const onWheel = (event) => {
          if (!this.ldDesktopMql.matches) return;

          const slides = this.getVisibleGallerySlides();
          if (slides.length <= 1) return;

          const delta =
            Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
          if (Math.abs(delta) < 1) return;

          event.preventDefault();
          slider.scrollLeft += delta;

          window.clearTimeout(this.ldWheelSnapTimer);
          this.ldWheelSnapTimer = window.setTimeout(() => {
            this.snapLdGalleryToNearest(!this.isLdDesktopGallery());
          }, 150);
        };

        dragSurface.addEventListener('wheel', onWheel, { passive: false });
      }

      syncLdActiveSlide(activeSlide) {
        if (!activeSlide) return;

        const mediaId = activeSlide.dataset.mediaId;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeSlide);
        });

        if (!mediaId || !this.elements.thumbnails) return;

        const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      snapLdGalleryToNearest(smooth = true) {
        const slides = this.getVisibleGallerySlides();
        if (!slides.length || !this.viewerSlider) return;

        const activeIndex = this.getNearestSlideIndex(slides, this.viewerSlider.scrollLeft);
        const targetSlide = slides[activeIndex];

        this.scrollLdGalleryToSlide(this.viewerSlider, targetSlide, smooth);
        this.syncLdActiveSlide(targetSlide);
      }

      onSlideChanged(event) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const isVisibleSlide = (element) =>
          element &&
          !element.hasAttribute('hidden') &&
          element.style.display !== 'none';

        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          Array.from(this.elements.viewer.querySelectorAll('[data-media-id]')).find(isVisibleSlide) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const slider = activeMedia.parentElement;
            if (this.isLdDesktopGallery()) {
              this.scrollLdGalleryToSlide(slider, activeMedia, false);
            } else {
              slider.scrollTo({
                left: activeMedia.offsetLeft,
                behavior: 'auto',
              });
            }
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        activeMedia.querySelectorAll('img').forEach((img) => {
          if (img.loading === 'lazy') img.loading = 'eager';
          if (!img.complete || img.naturalWidth === 0) {
            const src = img.currentSrc || img.src;
            if (src) img.src = src;
          }
        });

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img, .ciw-pdp-gallery__static-image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
